<script setup lang="ts">import { toRef as _toRef, computed as _computed, ref as _ref } from 'vue';

import { formatCurrency } from '@consumer/utils/currency'
import { isHolidayDesign } from '@consumer/utils/design'
import DesignButton from '@consumer/modules/multiPlaceGiftIdea/DesignButton.vue'
import type { GiftlyDesign, MultiplaceGift } from '@consumer/types/serializers'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import CustomAmountForm from '@consumer/modules/multiPlaceGiftIdea/CustomAmountForm.vue'
import { isDesktop } from '@consumer/utils/breakpoints'
import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import GButton from '@consumer/components/GButton.vue'
import { openCustomizerModal, openMultiplaceCustomizerModal } from '@consumer/services/modals'
import { useSharedData } from '@consumer/composables/sharedData'
import { useGiftTemplate } from '@consumer/composables/giftTemplate'
import { replaceQuery } from '@shared/url'
import { useWindowSize } from '@vueuse/core'
import CustomizerApi from '@consumer/api/CustomizerApi'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import { checkoutGift } from '@consumer/services/order'
import christmasWreath from '@consumer/assets/images/christmas-wreath.svg'
import newPlusIcon from '@consumer/assets/icons/new/plus.svg?svgicon'
import AmountButton from './AmountButton.vue'

withDefaults(defineProps<{
  multiPlaceGiftIdea: MultiplaceGift
  gift: GiftTemplate
  giftlyDesigns: GiftlyDesign[]
  isEditing?: boolean
  isEditingField?: boolean
  showSecondaryGiftlyImage: boolean
}>(), { isEditing: false,isEditingField: false, })

const emit = defineEmits<{ setAmount: [amount: number] }>()

let selectedDesign = (defineModel<GiftlyDesign>('selectedDesign', { required: true }))

const __$temp_1 = (useSharedData()),
  currentTheme = _toRef(__$temp_1, 'currentTheme');
const holidays = _computed(() => currentTheme.value === 'holidays')

// designs

let showAllDesigns = _ref(false)

let sortedDesigns = _ref(__props.giftlyDesigns)

const holidayDesigns = _computed(() =>
  sortedDesigns.value.filter(design => isHolidayDesign(design)))

const usualDesigns = _computed(() =>
  sortedDesigns.value.filter(design => !isHolidayDesign(design)))

const arrangedDesigns = _computed(() =>
  [...usualDesigns.value.slice(0, 2), ...holidayDesigns.value, ...usualDesigns.value.slice(2)])

const displayedDesigns = _computed(() => arrangedDesigns.value.slice(0, showAllDesigns.value ? 20 : 4))

// Re-arrange designs to make sure the selected design is always visible.
watch(() => displayedDesigns.value.includes(selectedDesign.value), (displayed) => {
  if (displayed) return
  const placeAt = !holidays.value || isHolidayDesign(selectedDesign.value) ? 3 : 1
  sortedDesigns.value = arrangedDesigns.value.filter(design => design !== selectedDesign.value)
  sortedDesigns.value.splice(placeAt, 0, selectedDesign.value)
}, { immediate: true })

const isPublished = _computed(() => __props.multiPlaceGiftIdea.status === 'published')

function selectDesign (design: GiftlyDesign) {
  selectedDesign.value = design
}

// amounts

const amounts = _computed(() => __props.multiPlaceGiftIdea.amounts)
const minimumAmount = 5
const maximumAmount = 1000

let selectedAmount = _ref(__props.gift?.amount || amounts.value[0])
let customAmount = _ref<number | false>(!amounts.value.includes(selectedAmount.value) && selectedAmount.value)
let customAmountModalOpen = _ref(false)
const customAmountFormRef = _ref<InstanceType<typeof CustomAmountForm>>()

function selectCustomAmount () {
  customAmountModalOpen.value = true
  nextTick(() => {
    customAmountFormRef.value?.focus()
  })
}

watchEffect(() => {
  emit('setAmount', selectedAmount.value)
})

function selectAmount (amount: number) {
  selectedAmount.value = amount
  customAmount.value = !amounts.value.includes(amount) && amount
  customAmountModalOpen.value = false
}

if (!__props.isEditing) {
  watch(() => [selectedAmount.value, selectedDesign.value.id] as const, ([amount, giftly_design_name]) => {
    replaceQuery({ amount, giftly_design_name })
  })
}

// continue to checkout
const __$temp_2 = (useGiftTemplate(() => __props.gift)),
  giftTemplateParam = _toRef(__$temp_2, 'giftTemplateParam');

function goToCheckout () {
  checkoutGift(
    { ...__props.gift, amount: selectedAmount.value },
    { specOptions: { purchaseMethod: 'multiplace' } },
  )
}

function openFullCustomizer () {
  openMultiplaceCustomizerModal({
    gift: __props.gift,
    multiplaceGift: __props.multiPlaceGiftIdea,
    createOrEditMultiplace: true,
  })
}

function updateStatus (published: boolean) {
  const status = __props.multiPlaceGiftIdea.status = published ? 'published' : 'draft'
  return MultiPlaceGiftIdeasApi.update({ params: __props.multiPlaceGiftIdea, data: { status } })
}

async function doneEditingClicked () {
  const { url } = await MultiPlaceGiftIdeasApi.finishEditing(__props.multiPlaceGiftIdea)
  if (!import.meta.env.VITEST) location.href = url
}

function editAmountsClicked () {
  openMultiplaceCustomizerModal({
    gift: __props.gift,
    multiplaceGift: __props.multiPlaceGiftIdea,
    createOrEditMultiplace: true,
    initialStep: 'Amount',
  })
}

const scrollingEl = ref()

watch(() => __props.showSecondaryGiftlyImage, async (newVal, oldVal) => {
  if (!oldVal && newVal) {
    await nextTick() // Wait for the render to determine the location of scrollingEl
    const { bottom: elBottom } = scrollingEl.value.getBoundingClientRect()
    const { height: windowHeight } = useWindowSize()
    if (elBottom > windowHeight.value) {
      showAllDesigns.value = false
    }
  }
})

const showBuyNowButton = computed(() => {
  if (__props.isEditing) {
    return !__props.isEditingField
  }
  else {
    return __props.multiPlaceGiftIdea.status === 'published'
  }
})

const showLinkToCustomizer = computed(() => {
  if (__props.isEditing) {
    return false
  }
  else {
    return __props.multiPlaceGiftIdea.status === 'published'
  }
})
</script>

<template>
  <div
    ref="scrollingEl"
    class="w-full lg:w-400px bg-white px-6 lg:px-8 py-6 pt-2 lg:py-6
    rounded-t-v2lg lg:rounded-v2lg relative"
    :style="{
      boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)',
    }"
  >
    <div
      class="flex flex-col gap-4 mx-auto max-w-md lg:max-w-full
        transform transition-all origin-top duration-200 ease-out"
      :class="{ 'h-0 scale-y-0 opacity-0': isDesktop && customAmountModalOpen }"
    >
      <template v-if="isEditing">
        <h2 class="text-xl my-2">
          Settings
          <div class="inline-block float-right text-base flex flex-col-2 items-center gap-1 font-light">
            <div>
              Status
            </div>
            <div>
              <GToggle
                id="published"
                :modelValue="isPublished"
                data-testid="published-switch"
                :label="isPublished ? 'Published': 'Unpublished'"
                @update:modelValue="updateStatus"
              />
            </div>
          </div>
        </h2>
        <div class="grid grid-cols-4 gap-2">
          <AmountButton
            v-for="amount in amounts"
            :key="amount"
            :amount="amount"
            :selectedAmount="selectedAmount"
          />
          <GButton
            name="Edit"
            size="small"
            secondary
            data-testid="edit-button-open-customizer"
            class="w-full !text-lg transition-all ease-in-out duration-200 !rounded-v2sm
            hover:border-2 !hover:border-primary-base !hover:bg-primary-pale border-solid
            !bg-grey-200 !text-grey-500 !border-grey-400"
            @click="editAmountsClicked"
          >
            edit
          </GButton>
        </div>
        <GButton
          v-if="!isEditingField"
          primary
          class="w-full"
          size="large"
          data-testid="done-editing-button"
          @click="doneEditingClicked"
        >Exit Edit Mode</GButton>
      </template>
      <div v-else class="flex flex-col">
        <h2 class="hidden lg:block text-xl">
          {{ holidays ? 'Customize gift' : 'Gift design & amount' }}
        </h2>
        <div class="grid grid-cols-5 gap-y-1 mt-2 transform transition-all origin-top duration-200 ease-out">
          <div
            v-if="holidays"
            class="absolute flex items-center font-semibold text-sm gap-1 text-v2-green-spruce whitespace-nowrap"
            style="background: #b4eaa7; border-radius: 84px; padding: 3px 8px; right: 62px; top: -28px;"
          >
            <img :src="christmasWreath" alt="Christmas wreath" height="17">
            <span style="color: #46623f; font-size: 0.75rem;">Holiday</span>
          </div>
          <DesignButton
            v-for="design in displayedDesigns"
            :key="design.id"
            :design="design"
            :isSelected="selectedDesign?.id === design.id"
            @click="selectDesign(design)"
          />

          <DesignButton
            v-if="!showAllDesigns"
            :holidays="holidays"
            aria-label="Show more designs"
            @click="showAllDesigns = true"
          >
            <i
              class="rounded-full w-full h-full flex items-center justify-center
              bg-grey-200 border-2 border-solid border-white"
              data-testid="design-button"
            >
              <GIcon :svg="newPlusIcon" size="40px" class="text-grey-500 hover:text-grey-800"/>
            </i>
          </DesignButton>
        </div>
        <div v-show="!showAllDesigns" class="grid grid-cols-4 gap-2 mt-4">
          <AmountButton
            v-for="amount in amounts"
            :key="amount"
            :amount="amount"
            :selectedAmount="selectedAmount"
            @selectAmount="selectAmount"
          />
          <GButton
            name="other-amount-button"
            size="small"
            secondary
            class="w-full !text-lg transition-all ease-in-out duration-200 !rounded-v2sm
            hover:border-2 !hover:border-primary-base !hover:bg-primary-pale border-solid"
            :class="[
              selectedAmount === customAmount ?
                '!border-primary-base !bg-primary-pale' :
                '!bg-grey-200 !text-grey-500 !border-grey-400',
            ]"
            @click="selectCustomAmount"
          >
            {{ customAmount ? formatCurrency(customAmount) : 'Other' }}
          </GButton>

        </div>
      </div>
      <div data-testid="button-section">
        <GButton
          v-if="showAllDesigns"
          name="save-design-button"
          size="large"
          primary
          class="w-full"
          data-testid="save-design-button"
          @click="showAllDesigns = false"
        >
          Save
        </GButton>
        <GButton
          v-else-if="isEditing && !isEditingField"
          name="customize-button"
          size="large"
          secondary
          class="w-full"
          data-testid="customize-button"
          @click="openFullCustomizer"
        >
          Edit Gift
        </GButton>
        <GButton
          v-else-if="showBuyNowButton"
          name="continue-button"
          size="large"
          primary
          class="w-full"
          data-testid="continue-button"
          @click="goToCheckout"
        >
          Buy Now
        </GButton>
        <div v-if="showLinkToCustomizer" class="text-center mt-5 text-primary-base text-lg font-medium">
          <a
            name="customize-button-flow"
            data-testid="customize-button-flow"
            :href="CustomizerApi.show.path({ query: { gift: giftTemplateParam } })"
            @click.exact.prevent="openCustomizerModal({ gift })"
          >
            Customize
          </a>
        </div>
      </div>
    </div>

    <div
      v-if="isDesktop"
      class="transform transition-all origin-top duration-200 ease-out relative"
      :class="{ 'h-0 scale-y-0': !customAmountModalOpen }"
    >
      <GIconButton
        id="close-modal-button"
        ref="closeButtonEl"
        name="close-modal-icon-button-custom-amount"
        class="-mr-3 g-dialog-close absolute z-dialog"
        style="right: -8px; top: -12px;"
        :icon="faClose"
        size="medium"
        label="Close Modal"
        @click="customAmountModalOpen = false"
      />
      <CustomAmountForm
        ref="customAmountFormRef"
        :minimumAmount="minimumAmount"
        :maximumAmount="maximumAmount"
        @save="selectAmount"
      />
    </div>

    <div v-if="!isDesktop">
      <GModal
        v-model:open="customAmountModalOpen"
        :fullscreenMobile="true"
        name="custom-amount-modal"
        class="w-full lg:w-md"
        @close="customAmountModalOpen = false"
      >
        <CustomAmountForm
          ref="customAmountFormRef"
          :minimumAmount="minimumAmount"
          :maximumAmount="maximumAmount"
          @save="selectAmount"
        />
      </GModal>
    </div>
  </div>
</template>
